import { normalizePackingScanCode } from './packingAssignKey';
import { Order, PackagingCategory, PackagingDetail, PackingPayloadPackagingItem } from '../models';
import { requirementUsesStationScan } from './packingStationPackaging';

export { requirementUsesStationScan } from './packingStationPackaging';

export type PackagingRequirementType = 'group' | 'exact';

export interface PackagingRequirement {
  /** 'group' (boxes) — cualquier packaging del mismo grupo lo satisface. 'exact' (marketing/sin grupo) — debe coincidir el packaging_id. */
  type: PackagingRequirementType;
  /** Identificador estable para asignar progreso (groupId para 'group', packagingId para 'exact') */
  key: string;
  /** Unidades totales requeridas por la orden para este requerimiento */
  qty: number;
  /** IDs de packagings que satisfacen este requerimiento */
  members: number[];
  /** Nombre legible (nombre del grupo o del packaging) */
  name: string;
  /** Categoría conocida (para mostrar etiqueta visual) */
  category?: PackagingCategory;
  /** Opciones concretas permitidas para este requerimiento (útil para mostrar imagen/nombre de la caja escaneada) */
  options?: Array<{
    packagingId: number;
    productId?: number;
    code?: string;
    name?: string;
    imageUrl?: string;
  }>;
}

export const PACKAGING_KEY_PREFIX = 'pkg';

const isBoxCategory = (cat?: PackagingCategory) => cat === 'own_box' || cat === 'customer_box';

const sanitizePackagingName = (value?: string | null): string | null => {
  if (!value) return null;
  const normalized = value.trim();
  if (!normalized) return null;
  if (normalized.toLowerCase() === 'null') return null;
  if (normalized.toLowerCase() === 'packaging null') return null;
  return normalized;
};

const buildImageUrlFromHash = (imageHash?: string | null): string | undefined => {
  if (!imageHash) return undefined;
  if (imageHash.startsWith('http')) return imageHash;
  return `https://warehouse-images-stg-dev.nyc3.digitaloceanspaces.com/${imageHash}`;
};

const findPackagingDetailForPayload = (
  packagings: PackagingDetail[],
  option: { packagingId: number; productId?: number },
): PackagingDetail | undefined => {
  return packagings.find((p) => {
    if (p.id === option.packagingId) return true;
    if (option.productId != null && p.id === option.productId) return true;
    if (option.productId != null && p.product_id === option.productId) return true;
    return false;
  });
};

export const packagingAssignKey = (orderId: number, packagingId: number) =>
  `${orderId}:${PACKAGING_KEY_PREFIX}:${packagingId}`;

/** Calcula los requerimientos de packaging para una orden, agrupando cajas por box_group. */
export const computeOrderPackagingRequirements = (
  order: Order | null | undefined,
  packagings: PackagingDetail[],
): PackagingRequirement[] => {
  if (!order || !order.order_packagings || order.order_packagings.length === 0) return [];

  const groupReqs = new Map<
    number,
    {
      qty: number;
      name: string;
      members: Set<number>;
      optionsByPackagingId: Map<
        number,
        { packagingId: number; productId?: number; code?: string; name?: string; imageUrl?: string }
      >;
      category?: PackagingCategory;
    }
  >();
  const exactReqs = new Map<
    number,
    {
      qty: number;
      name: string;
      category?: PackagingCategory;
      optionsByPackagingId: Map<
        number,
        { packagingId: number; productId?: number; code?: string; name?: string; imageUrl?: string }
      >;
    }
  >();

  for (const op of order.order_packagings) {
    const detail = op.packaging_id != null ? packagings.find((p) => p.id === op.packaging_id) : undefined;
    const groupId = op.box_group_id ?? detail?.packaging_box_group_id ?? null;
    const groupPackagingsFromOrder = op.box_group?.packagings || [];
    const groupMembersFromOrder = groupPackagingsFromOrder.map((p) => p.id).filter((id) => Number.isFinite(id));
    const groupMembersFromCatalog =
      groupId != null ? packagings.filter((p) => p.packaging_box_group_id === groupId).map((p) => p.id) : [];
    const catalogGroupCategory =
      groupId != null ? packagings.find((p) => p.packaging_box_group_id === groupId)?.category : undefined;
    const groupMembers = groupMembersFromOrder.length > 0 ? groupMembersFromOrder : groupMembersFromCatalog;
    const groupCategory =
      groupPackagingsFromOrder[0]?.category ||
      detail?.category ||
      catalogGroupCategory ||
      (op.packaging_id == null && groupId != null ? 'customer_box' : undefined);

    if ((groupId != null && groupMembers.length > 0) || (groupId != null && isBoxCategory(groupCategory))) {
      const groupName =
        sanitizePackagingName(op.box_group?.name) || detail?.box_group?.name || `Grupo de cajas #${groupId}`;
      const optionsFromOrder = groupPackagingsFromOrder.map((g) => {
        const catalogMatch = packagings.find(
          (p) => p.id === g.id || p.id === g.product_id || p.product_id === g.product_id,
        );
        const productName = sanitizePackagingName(g.product?.name);
        const catalogName = sanitizePackagingName(catalogMatch?.name);
        return {
          packagingId: g.id,
          productId: g.product_id ?? g.product?.id ?? undefined,
          code: g.product?.code || undefined,
          name: productName || catalogName || undefined,
          imageUrl:
            g.product?.image_url ||
            buildImageUrlFromHash(g.product?.image_hash) ||
            catalogMatch?.image_url ||
            undefined,
        };
      });
      const optionsFromCatalog = groupMembersFromCatalog.map((memberId) => {
        const catalogMatch = packagings.find((p) => p.id === memberId);
        return {
          packagingId: memberId,
          code: catalogMatch?.code || undefined,
          name: sanitizePackagingName(catalogMatch?.name) || undefined,
          imageUrl: catalogMatch?.image_url || undefined,
        };
      });

      const existing = groupReqs.get(groupId);
      if (existing) {
        existing.qty += op.qty;
        for (const m of groupMembers) existing.members.add(m);
        for (const opt of [...optionsFromOrder, ...optionsFromCatalog]) {
          if (opt.packagingId) existing.optionsByPackagingId.set(opt.packagingId, opt);
        }
      } else {
        const optionsByPackagingId = new Map<
          number,
          { packagingId: number; productId?: number; code?: string; name?: string; imageUrl?: string }
        >();
        for (const opt of [...optionsFromOrder, ...optionsFromCatalog]) {
          if (opt.packagingId) optionsByPackagingId.set(opt.packagingId, opt);
        }
        groupReqs.set(groupId, {
          qty: op.qty,
          name: groupName,
          members: new Set(groupMembers),
          optionsByPackagingId,
          category: groupCategory,
        });
      }
    } else {
      const detailName = sanitizePackagingName(detail?.name);
      const orderPackagingName = sanitizePackagingName(op.packaging?.name);
      const hasValidPackagingId = typeof op.packaging_id === 'number' && Number.isFinite(op.packaging_id);
      const name = detailName || orderPackagingName || (hasValidPackagingId ? `Packaging ${op.packaging_id}` : '');
      if (op.packaging_id == null) continue;
      const existing = exactReqs.get(op.packaging_id);
      if (existing) {
        existing.qty += op.qty;
      } else {
        const optionsByPackagingId = new Map<
          number,
          { packagingId: number; productId?: number; code?: string; name?: string; imageUrl?: string }
        >();
        optionsByPackagingId.set(op.packaging_id, {
          packagingId: op.packaging_id,
          code: detail?.code || undefined,
          name,
          imageUrl: detail?.image_url || undefined,
        });
        exactReqs.set(op.packaging_id, { qty: op.qty, name, category: detail?.category, optionsByPackagingId });
      }
    }
  }

  const requirements: PackagingRequirement[] = [];

  for (const [groupId, info] of groupReqs) {
    requirements.push({
      type: 'group',
      key: `group:${groupId}`,
      qty: info.qty,
      members: Array.from(info.members),
      name: info.name,
      category: info.category || 'own_box',
      options: Array.from(info.optionsByPackagingId.values()),
    });
  }

  for (const [packagingId, info] of exactReqs) {
    requirements.push({
      type: 'exact',
      key: `exact:${packagingId}`,
      qty: info.qty,
      members: [packagingId],
      name: info.name,
      category: info.category,
      options: Array.from(info.optionsByPackagingId.values()),
    });
  }

  return requirements;
};

/** Suma las unidades escaneadas (en `assignedPackagingQuantities`) que satisfacen un requerimiento concreto. */
export const sumAssignedForRequirement = (
  req: PackagingRequirement,
  orderId: number,
  assignedPackagingQuantities: Record<string, number>,
): number => {
  return req.members.reduce(
    (sum, memberId) => sum + (assignedPackagingQuantities[packagingAssignKey(orderId, memberId)] || 0),
    0,
  );
};

/** Devuelve true si todos los requerimientos de packaging del pedido están escaneados completamente. */
export const areAllPackagingsCompleted = (
  order: Order | null | undefined,
  packagings: PackagingDetail[],
  assignedPackagingQuantities: Record<string, number>,
): boolean => {
  const requirements = computeOrderPackagingRequirements(order, packagings);
  if (requirements.length === 0) return true;
  return requirements.every((req) => sumAssignedForRequirement(req, order!.id, assignedPackagingQuantities) >= req.qty);
};

/** Encuentra un packaging del catálogo cuyo code/name/ean coincida con el código escaneado. */
export const findPackagingByCode = (
  packagings: PackagingDetail[],
  scannedCode: string,
): PackagingDetail | undefined => {
  const code = normalizePackingScanCode(scannedCode);
  if (!code) return undefined;
  return packagings.find((p) => {
    const candidates = [p.code, p.ean, p.name].filter(Boolean) as string[];
    return candidates.some((c) => normalizePackingScanCode(c) === code);
  });
};

/** Localiza el primer requerimiento del pedido que el packaging escaneado puede satisfacer y que no esté completo. */
export const findRequirementToFulfill = (
  scanned: PackagingDetail,
  order: Order,
  packagings: PackagingDetail[],
  assignedPackagingQuantities: Record<string, number>,
):
  | {
      requirement: PackagingRequirement;
      packagingIdToAssign: number;
      payloadPackagingId: number;
      payloadItem?: Omit<PackingPayloadPackagingItem, 'qty'>;
    }
  | undefined => {
  const requirements = computeOrderPackagingRequirements(order, packagings);
  const scannedName = scanned.name ? normalizePackingScanCode(scanned.name) : undefined;
  const scannedCode = scanned.code ? normalizePackingScanCode(scanned.code) : undefined;
  for (const req of requirements) {
    // customer_box y marketing_item se escanean por ubicación del puesto, no por código de packaging
    if (requirementUsesStationScan(req)) continue;

    let packagingIdToAssign: number | undefined;
    let matchedOption: NonNullable<PackagingRequirement['options']>[number] | undefined;
    if (req.members.includes(scanned.id)) {
      packagingIdToAssign = scanned.id;
      matchedOption = (req.options || []).find(
        (opt) => opt.packagingId === scanned.id || opt.productId === scanned.id,
      ) || {
        packagingId: scanned.id,
        productId: scanned.product_id ?? undefined,
        code: scanned.code,
        name: scanned.name,
        imageUrl: scanned.image_url ?? undefined,
      };
    } else {
      matchedOption = (req.options || []).find((opt) => {
        if (opt.packagingId === scanned.id) return true;
        if (opt.productId === scanned.id) return true;
        if (scanned.product_id != null && opt.productId === scanned.product_id) return true;
        if (scannedCode && opt.code && normalizePackingScanCode(opt.code) === scannedCode) return true;
        if (scannedName && opt.name && normalizePackingScanCode(opt.name) === scannedName) return true;
        return false;
      });
      packagingIdToAssign = matchedOption?.packagingId;
    }
    if (!packagingIdToAssign) continue;
    const assigned = sumAssignedForRequirement(req, order.id, assignedPackagingQuantities);
    if (assigned < req.qty) {
      const detail = matchedOption ? findPackagingDetailForPayload(packagings, matchedOption) : undefined;
      const locationId = detail?.locations?.[0]?.location_id ?? scanned.locations?.[0]?.location_id;
      const payloadPackagingId = matchedOption?.productId ?? scanned.product_id ?? packagingIdToAssign;
      return {
        requirement: req,
        packagingIdToAssign,
        payloadPackagingId,
        ...(locationId != null
          ? {
              payloadItem: {
                packaging_id: payloadPackagingId,
                location_id: locationId,
              },
            }
          : {}),
      };
    }
  }
  return undefined;
};

/** Construye el array `packaging[]` a enviar en el payload de pack, eligiendo siempre la primera ubicación disponible. */
export const buildPackagingPayload = (
  order: Order | null | undefined,
  orderId: number,
  packagings: PackagingDetail[],
  assignedPackagingQuantities: Record<string, number>,
): PackingPayloadPackagingItem[] => {
  if (!order) return [];

  const result: PackingPayloadPackagingItem[] = [];
  const requirements = computeOrderPackagingRequirements(order, packagings);

  for (const req of requirements) {
    const optionCounts = (req.options || [])
      .map((opt) => {
        const scannedQty = assignedPackagingQuantities[packagingAssignKey(orderId, opt.packagingId)] || 0;
        return { opt, scannedQty };
      })
      .filter((item) => item.scannedQty > 0);

    for (const { opt, scannedQty } of optionCounts) {
      const detail = findPackagingDetailForPayload(packagings, opt);
      const locationId = detail?.locations?.[0]?.location_id;
      if (locationId == null) continue;

      // En grupos de cajas, backend espera el product_id (ej: 2471). En otros casos, usar packagingId.
      const payloadPackagingId = opt.productId ?? opt.packagingId;
      result.push({ packaging_id: payloadPackagingId, qty: scannedQty, location_id: locationId });
    }
  }

  return result;
};
